import type { ReactElement } from 'react';
import { EmberMascot } from './EmberMascot';
import type { MascotFace } from '../../../../../shared/ember-mascot.js';
import './PageFailure.css';
import { PageBack } from './PageBack';
import { PageHome } from './PageHome';

interface PageFailureProps {
  title: string;
  detail?: string;
  /**
   * Which broken arrival this is (ui.seen `page_failure`, props.state):
   * not_found | load_failed | voice_timeout | not_yours | spa_404 | … —
   * route says where, this says what. Every call site passes one; the
   * fallback keeps an unnamed state countable rather than dropped.
   */
  logState?: string;
  /** The page's own back/Home row; false when the caller draws its header above (a room's page: back to Chats). */
  toolbar?: boolean;
  /** A Try again the eye reads as a button (2026-10-27 Riley): the page is read again from the top. */
  retry?: boolean;
}

/** Shared full-page failure/missing state: back at the top-left like every
 * other page, message centered, a quiet ember illustration underneath. */
export function PageFailure({ title, detail = '', logState = 'unspecified', face, toolbar = true, retry = false }: PageFailureProps & { face?: MascotFace }): ReactElement {
  return (
    <main className="ember-page-failure" role="status" data-log-seen="page_failure" data-log-state={logState}>
      {toolbar && <div className="ember-page-failure-toolbar">
        <PageBack className="ember-page-failure-back" />
        {/* A stranded arrival's surest exit: Back retraces a trail that just
            broke, Home always lands somewhere whole. */}
        <div className="ember-toolbar-actions">
          <PageHome />
        </div>
      </div>}
      <div className="ember-page-failure-content">
        <h1>{title}</h1>
        {detail && <p>{detail}</p>}
        {retry && (
          <button type="button" className="ember-btn ember-btn-primary ember-page-failure-retry" data-log="page_failure_retry" data-log-state={logState} onClick={() => window.location.reload()}>
            Try again
          </button>
        )}
        {face ? <EmberMascot face={face} interactive={false} className="ember-page-failure-mascot" /> : (
        <svg className="ember-page-failure-art" viewBox="0 0 140 104" width="140" height="104" fill="none" aria-hidden>
          <path
            className="ember-page-failure-art-flame"
            d="M70 22c-13 15-20 25.5-20 37a20 20 0 0 0 40 0c0-11.5-7-22-20-37Z"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            className="ember-page-failure-art-core"
            d="M70 51c-5.5 6.5-8.5 11-8.5 16a8.5 8.5 0 0 0 17 0c0-5-3-9.5-8.5-16Z"
          />
          <circle className="ember-page-failure-art-spark" cx="99" cy="34" r="2.4" />
          <circle className="ember-page-failure-art-spark" cx="42" cy="28" r="1.8" />
          <circle className="ember-page-failure-art-spark" cx="108" cy="58" r="1.5" />
          <path className="ember-page-failure-art-ground" d="M42 92h56" strokeWidth="2" strokeLinecap="round" />
        </svg>
        )}
      </div>
    </main>
  );
}
